import React, { useState } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';
import { Select, NumberInput } from '../ui/Input';
import { POST_SIZES, POST_STYLES, POST_MATERIALS, POST_BASE_TYPES, POST_BEARINGS } from '../../constants/posts';

/**
 * PostEditor - Editor panel for porch / structural post properties
 */
export const PostEditor = ({
  post,
  onUpdate,
  onDelete,
  onDuplicate,
  onClose,
  x = 20,
  y = 100,
  isMobile = false,
}) => {
  const [minimized, setMinimized] = useState(isMobile);

  if (!post) return null;

  const toOptions = (source, format) => Object.entries(source).map(([value, val]) => ({
    value,
    label: format(val),
  }));

  const sizeOptions = toOptions(POST_SIZES, v => v.label);
  const styleOptions = toOptions(POST_STYLES, v => v.label);
  const materialOptions = toOptions(POST_MATERIALS, v => v.label);
  const baseOptions = toOptions(POST_BASE_TYPES, v => v.label);
  const bearingOptions = toOptions(POST_BEARINGS, v => v.label);

  const footprint = POST_SIZES[post.size] || POST_SIZES['6x6'];

  return (
    <FloatingPanel
      title="Post Properties"
      onClose={onClose}
      onMinimize={() => setMinimized(!minimized)}
      minimized={minimized}
      isMobile={isMobile}
      x={x}
      y={y}
      maxHeight={420}
    >
      <div style={{ padding: '12px' }}>
        <PanelSection title="Size">
          <Select
            value={post.size || '6x6'}
            onChange={(size) => onUpdate({ size })}
            options={sizeOptions}
          />
          <div style={{ color: '#6080a0', fontSize: '10px', marginTop: '-6px', marginBottom: '10px' }}>
            Actual {footprint.width}" × {footprint.depth}"
            {footprint.shape === 'round' ? ' round' : ''}
          </div>
          <NumberInput
            label="Height"
            value={Math.round((post.height || 96) / 12 * 10) / 10}
            onChange={(feet) => onUpdate({ height: Math.round(feet * 12) })}
            min={2}
            max={24}
            step={0.5}
            suffix="ft"
          />
          <div style={{ color: '#6080a0', fontSize: '10px', marginTop: '-6px', marginBottom: '10px' }}>
            Used only where no roof stands over the post - otherwise it runs up to
            the underside of that roof's fascia
          </div>
          <Select
            label="Bears On"
            value={post.bearing || 'grade'}
            onChange={(bearing) => onUpdate({ bearing })}
            options={bearingOptions}
          />
        </PanelSection>

        <PanelSection title="Appearance">
          <Select
            label="Style"
            value={post.style || 'plain'}
            onChange={(style) => onUpdate({ style })}
            options={styleOptions}
          />
          <div style={{ color: '#6080a0', fontSize: '10px', marginTop: '-6px', marginBottom: '10px' }}>
            {POST_STYLES[post.style || 'plain']?.description}
          </div>
          <Select
            label="Material"
            value={post.material || 'pt'}
            onChange={(material) => onUpdate({ material })}
            options={materialOptions}
          />
          <Select
            label="Base"
            value={post.base || 'standoff'}
            onChange={(base) => onUpdate({ base })}
            options={baseOptions}
          />
          <NumberInput
            label="Rotation"
            value={Math.round(post.rotation || 0)}
            onChange={(rotation) => onUpdate({ rotation })}
            min={0}
            max={359}
            step={15}
            suffix="deg"
          />
        </PanelSection>

        <PanelSection title="Position">
          <NumberInput
            label="X"
            value={Math.round(post.x)}
            onChange={(val) => onUpdate({ x: val })}
            step={1}
          />
          <NumberInput
            label="Y"
            value={Math.round(post.y)}
            onChange={(val) => onUpdate({ y: val })}
            step={1}
          />
        </PanelSection>

        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
          {onDuplicate && (
            <Button variant="default" onClick={onDuplicate} style={{ flex: 1 }}>
              Duplicate
            </Button>
          )}
          <Button variant="danger" onClick={onDelete} style={{ flex: 1 }}>
            Delete Post
          </Button>
        </div>
      </div>
    </FloatingPanel>
  );
};

export default PostEditor;
